import { Block, CheckCircle, Clear } from "@mui/icons-material";
import GetAppIcon from "@mui/icons-material/GetApp";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { format, isValid, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { MaterialReactTable, MRT_SelectCheckbox } from "material-react-table";
import moment from "moment";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { getConfidentialFields } from "../../../actions/fields";
import { auditarExportacion, CODIGO_AUDITORIA } from "../../../actions/audit";
import { getFormatoFechaExportacion } from "../../../utils/datetimeUtils";
import { downloadCsv } from "../../../utils/filesUtils";
import snackbarUtils from "../../../utils/snackbarUtils";
import { redactFromRegistroConfidentialFields } from "../../../utils/util";
import { StyledAppointments } from "../../styled/appointments";
import { tableConfig } from "./config/tableConfig";
import TitleContainer from "./utilsTable/titleWithFetchData";

const MaterialTableComponent = ({
  data,
  loading,
  time,
  handleChangeTime,
  fetchData,
  title,
  noRefresh,
  cancelledAction,
  attendedAction,
  unAttendedAction,
  handleAccidentDetail,
  displayAccidents,
}) => {
  const dispatch = useDispatch();
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  const columnsBase = [
    {
      header: "Fecha",
      enableColumnActions: false,
      accessorKey: "fechaHora",
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        const cellDate = parseISO(row.original[columnId]);
        if (!isValid(cellDate)) return false;
        return format(cellDate, "dd/MM/yyyy", { locale: es }) === filterValue;
      },
      Cell: ({ row }) => {
        const date = parseISO(row.original.fechaHora);
        return isValid(date) ? format(date, "dd/MM/yyyy", { locale: es }) : "";
      },
      Filter: ({ column }) => {
        const [selectedDate, setSelectedDate] = useState(null);

        return (
          <DatePicker
            value={selectedDate}
            slotProps={{
              textField: {
                variant: "standard",
                sx: {
                  "& .MuiInputBase-root": {
                    minHeight: 35,
                  },
                  "& .MuiOutlinedInput-input ": {
                    height: 5,
                  },
                  "& .MuiChip-root": {
                    height: 1,
                  },
                },
              },
            }}
            onChange={(newDate) => {
              setSelectedDate(newDate);
              column.setFilterValue(
                newDate
                  ? format(newDate, "dd/MM/yyyy", { locale: es })
                  : undefined,
              );
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" fullWidth />
            )}
          />
        );
      },
    },
    {
      header: "Hora",
      accessorKey: "hora",
      enableColumnActions: false,
      filterFn: (row, columnId, filterValue) => {
        const cellValue = moment(row.getValue("fechaHora")).format("HH:mm");
        return cellValue.includes(filterValue); // Comprobamos si la hora incluye el valor del filtro
      },
      Cell: (rowData) => {
        const time = rowData.row.original.fechaHora;
        return time ? moment(time).format("HH:mm") : ""; // Mostramos solo la hora
      },
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              minHeight: 35,
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
    },
    {
      header: "Trabajador",
      accessorFn: (row) =>
        `${row.apellidoTrabajador || ""}, ${row.nombreTrabajador || ""}`,
      enableColumnActions: false,

      filterFn: (row, columnId, filterValue) => {
        const fullName = `${row.original.apellidoTrabajador || ""}, ${row.original.nombreTrabajador || ""
          }`.toLowerCase();

        return fullName.includes(filterValue.toLowerCase());
      },
      Cell: ({ row }) =>
        `${row.original.apellidoTrabajador}, ${row.original.nombreTrabajador}`,
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
      },
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              minHeight: 35,
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
    },
    {
      header: "Documento",
      enableColumnActions: false,
      accessorKey: "nroDocumento",
      Cell: (rowData) => rowData.row.original.nroDocumento,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              minHeight: 35,
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
    },
    {
      header: "Observaciones",
      enableColumnActions: false,
      accessorKey: "observaciones",
      Cell: (rowData) => rowData.row.original.observaciones,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              minHeight: 35,
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
    },
  ];

  const columnsActions = [
    {
      header: "Acciones",
      accessorKey: "acciones",
      enableColumnFilter: false,
      enableColumnActions: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <Tooltip
          title="Ver detalles"
          onClick={() => handleAccidentDetail(row.original)}>
          <StyledAppointments.VisibilityIcon />
        </Tooltip>
      ),
    },
  ];

  const columnsSearch = [
    {
      header: "Estado",
      accessorKey: "estado",
      enableColumnActions: false,
      Cell: (rowData) => rowData.row.original.estado,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              minHeight: 35,
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
    },
    {
      header: "Profesional",
      accessorKey: "profesional",
      enableColumnActions: false,
      Cell: (rowData) => rowData.row.original.profesional.trim(),
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              minHeight: 35,
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
      },
    },
    {
      header: "Especialidad",
      accessorKey: "especialidad",
      enableColumnActions: false,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              minHeight: 35,
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
      },
      Cell: (rowData) => rowData.row.original.especialidad.trim(),
    },
    {
      header: "Nro. Siniestro",
      enableColumnActions: false,
      accessorKey: "nroSiniestro",
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              minHeight: 35,
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
      Cell: (rowData) => rowData.row.original.nroSiniestro.trim(),
    },
    ...columnsBase,
    ...columnsActions,
  ];

  const columnsPending = [
    {
      header: "Art",
      accessorKey: "art",
      customFilterAndSearch: null,
      enableColumnActions: false,
      Cell: (rowData) => rowData.row.original.art.toUpperCase(),
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              minHeight: 35,
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
    },
    {
      header: "Profesional",
      accessorKey: "profesional",
      customFilterAndSearch: null,
      enableColumnActions: false,
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
      },
      Cell: (rowData) => rowData.row.original.profesional,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              minHeight: 35,
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
    },
    {
      header: "Especialidad",
      accessorKey: "especialidad",
      enableColumnActions: false,
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
      },
      customFilterAndSearch: null,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              minHeight: 35,
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
      Cell: (rowData) => rowData.row.original.especialidad,
    },
    ...columnsBase,
    ...columnsActions,
  ];

  const columnOrder =
    title === "Asistencias pendientes de informar"
      ? [
        "Art",
        "Fecha",
        "Hora",
        "Profesional",
        "Especialidad",
        "Siniestro",
        "Trabajador",
        "Documento",
        "Observaciones",
        "Acciones",
      ]
      : [
        "Estado",
        "Fecha",
        "Hora",
        "Profesional",
        "Especialidad",
        "Siniestro",
        "Trabajador",
        "Documento",
        "Observaciones",
        "Acciones",
      ];

  const formatTableDataForExport = (table) => {
    return table.map((fila) => [
      fila.art ? `"${fila.art}"` : "",
      fila.estado ? `"${fila.estado}"` : "",
      getFormatoFechaExportacion(fila.fechaHora, "dd/MM/yyyy"),
      getFormatoFechaExportacion(fila.fechaHora, "HH:mm"),
      fila.profesional ? `"${fila.profesional}"` : "",
      fila.especialidad ? `"${fila.especialidad}"` : "",
      fila.nroSiniestro ? `"${fila.nroSiniestro}"` : "",
      `${fila.nombreTrabajador} ${fila.apellidoTrabajador}`,
      fila.nroDocumento ? `"${fila.nroDocumento}"` : "",
      fila.observaciones ? `"${fila.observaciones}"` : "",
    ]);
  };

  const handleExportToCsv = () => {
    setIsDownloadingCsv(true);
    dispatch(auditarExportacion(data, "mlCloudCoreArtId", CODIGO_AUDITORIA.EXPORTACION_TURNOS))
      .then(() => dispatch(getConfidentialFields("turnos")))
      .then((res) => {
        const redactedData = data.map((row) =>
          redactFromRegistroConfidentialFields(
            row,
            res.value.data,
            "mlCloudCoreArtId",
          ),
        );
        const formattedDataAsCsv = [
          [
            "Art",
            "Estado",
            "Fecha",
            "Hora",
            "Profesional",
            "Especialidad",
            "Siniestro",
            "Trabajador",
            "Documento",
            "Observaciones",
          ],
          ...formatTableDataForExport(redactedData),
        ];
        let csvContent =
          "sep=,\n" + formattedDataAsCsv.map((e) => e.join(",")).join("\n");
        downloadCsv(csvContent, "Turnos");
        snackbarUtils.success("Los turnos han sido descargados con éxito.");
        setIsDownloadingCsv(false);
      })
      .catch((err) => {
        snackbarUtils.error(err);
        setIsDownloadingCsv(false);
      });
  };

  return (
    <>
      {displayAccidents !== "block" ? (
        <></>
      ) : (
        <MaterialReactTable
          {...tableConfig}
          columns={
            title === "Asistencias pendientes de informar"
              ? columnsPending
              : columnsSearch
          }
          enableRowSelection={
            title === "Asistencias pendientes de informar" ? true : false
          }
          data={data}
          state={{ isLoading: loading || isDownloadingCsv }}
          renderToolbarAlertBannerContent={({ selectedAlert, table }) => (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}>
              <>
                <MRT_SelectCheckbox table={table} />
                {selectedAlert}
              </>
              <StyledAppointments.ActionsBox>
                <Tooltip title="Asistió">
                  <IconButton
                    sx={{ color: "#4caf50" }}
                    onClick={() => {
                      attendedAction(table.getSelectedRowModel().rows); // Obtén solo las filas seleccionadas
                      table.resetRowSelection(); // Resetea la selección
                    }}>
                    <CheckCircle />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancelar turno">
                  <IconButton
                    color="inherit"
                    onClick={() => {
                      cancelledAction(table.getSelectedRowModel().rows); // Obtén solo las filas seleccionadas
                      table.resetRowSelection(); // Resetea la selección
                    }}>
                    <Clear />
                  </IconButton>
                </Tooltip>
                <Tooltip title="No asistió">
                  <IconButton
                    sx={{ color: "#f14411" }}
                    onClick={() => {
                      unAttendedAction(table.getSelectedRowModel().rows); // Obtén solo las filas seleccionadas
                      table.resetRowSelection(); // Resetea la selección
                    }}>
                    <Block />
                  </IconButton>
                </Tooltip>
              </StyledAppointments.ActionsBox>
            </Box>
          )}
          renderTopToolbarCustomActions={({ table }) => (
            <Box m={0.5}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center">
                <TitleContainer
                  title={title}
                  noRefresh={noRefresh}
                  fetchData={fetchData}
                />
                <Box>
                  <Tooltip title="Exportar CSV">
                    <IconButton
                      color="inherit"
                      onClick={handleExportToCsv}
                      disabled={isDownloadingCsv}>
                      <GetAppIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {time && (
                <div>
                  <FormControl
                    sx={{ margin: 1, minWidth: 250 }}
                    variant="standard">
                    <InputLabel>Duración</InputLabel>
                    <Select onChange={handleChangeTime} value={time}>
                      <MenuItem value={2}>48 horas</MenuItem>
                      <MenuItem value={7}>7 días</MenuItem>
                      <MenuItem value={30}>30 días</MenuItem>
                      <MenuItem value={100}>100 días</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              )}
            </Box>
          )}
          initialState={{
            density: "compact",
            showColumnFilters: true,
            columnOrder: ["mrt-row-select", ...columnOrder],
          }}
        />
      )}
    </>
  );
};

export default MaterialTableComponent;
